import { ColumnPinningPosition } from '@tanstack/react-table';
import { omit } from 'lodash';

import { DisposalInterestIndexItem, PartialDisposalInterestIndexItem } from '@shared/apis/disposals';
import { IDropdownItem } from '@shared/components/dropdown-new';
import { InterestScheduleFilterParams } from '@shared/components/interest-schedule/interest-schedule-filter-modal';
import { ReportingInterestColumnKeys } from '@shared/components/interest-schedule/reporting-interest-table';
import { Option } from '@shared/components/select-new/Select';
import { TableColumn } from '@shared/components/virtual-table';
import { t } from '@shared/i18n';
import {
  InterestScheduleConfig,
  interestScheduleConfigGroupKeys,
  InterestScheduleContextConfig,
  InterestScheduleCustomAction,
  InterestScheduleStatuses,
} from '@shared/types/common/match';
import {
  SCHEDULE_MATCH_ALL_GROUP,
  ScheduleMatchStatusGroups,
  ScheduleMatchStatusGroupsWithAll,
} from '@shared/types/common/match';

// ---- Types ----

export enum GroupdByFilter {
  Starred = 'starred',
  Status = 'status',
}

export type InterestScheduleStatusDropdownOption = IDropdownItem<
  InterestScheduleStatuses | InterestScheduleCustomAction | ''
>;

export enum InterestTypeFilter {
  All = 'all',
  InterestByInstructedAgents = 'interest_by_instructed_agents',
  InterestWithoutRequirements = 'interest_without_requirements',
  InterestWithSocietyRequirements = 'interest_with_society_requirements',
  JointAgentInterest = 'joint_agent_interest',
  OurInterest = 'our_interest',
  Starred = 'starred',
}

export interface OrderByConfig {
  key: 'timestamp_requirement_date' | 'timestamp_interest_schedule_updated_at' | 'timestamp_match_created';
  reverse: boolean;
  exportKey: string;
}

// ---- Local Utils ----

const filterInterestByTypeFilter = (interest: DisposalInterestIndexItem, typeFilter: InterestTypeFilter): boolean => {
  switch (typeFilter) {
    case InterestTypeFilter.All:
      return true;

    case InterestTypeFilter.InterestWithoutRequirements:
      return interest.is_naked_interest;

    case InterestTypeFilter.InterestWithSocietyRequirements:
      return interest.is_society_match;

    case InterestTypeFilter.JointAgentInterest:
      return interest.is_external_match && !interest.is_society_match;

    case InterestTypeFilter.InterestByInstructedAgents:
      return interest.is_external_match;

    case InterestTypeFilter.OurInterest:
      return !interest.is_external_match && !interest.is_society_match;

    case InterestTypeFilter.Starred:
      return interest.fav;
  }
};

// ---- Exported Utils ----

export const filterMatchByStatusGroup = (
  interest: DisposalInterestIndexItem,
  currentStatusGroup: ScheduleMatchStatusGroupsWithAll,
  contextConfig: InterestScheduleContextConfig
): boolean => {
  const itemStatusGroup = contextConfig.statusGroupsMap[interest.interest_schedule_status];

  // Check whether we can add the match under the current tab
  switch (currentStatusGroup) {
    case SCHEDULE_MATCH_ALL_GROUP:
      return contextConfig.statusGroupsActive.includes(itemStatusGroup) || typeof itemStatusGroup === 'undefined';

    case ScheduleMatchStatusGroups.Discounted:
      return !!interest.interest_schedule_discounted_reason;

    default:
      return currentStatusGroup === itemStatusGroup;
  }
};

export const filterInterestByFilters = (
  interest: DisposalInterestIndexItem,
  filterParams: InterestScheduleFilterParams
): boolean => {
  // ---- AND filters ----

  // Don't include interest that doesn't match the interest type
  if (!filterInterestByTypeFilter(interest, filterParams.interestType)) {
    return false;
  }

  // ---- OR filters ----

  if (interest.interest_schedule_hidden) {
    return filterParams.includeArchived;
  }

  if (interest.interest_schedule_discounted_reason) {
    return filterParams.includeDiscounted;
  }

  return true;
};

export const getInterestScheduleOrderByConfig = (orderBy: string): OrderByConfig => {
  switch (orderBy) {
    case 'date_of_requirement-desc':
      return { exportKey: 'timestamp_requirement_date-desc', key: 'timestamp_requirement_date', reverse: true };

    case 'updated_at-desc':
      return {
        exportKey: 'timestamp_interest_schedule_updated_at-desc',
        key: 'timestamp_interest_schedule_updated_at',
        reverse: true,
      };

    case 'created_at-desc':
    default:
      return { exportKey: 'timestamp_match_created-desc', key: 'timestamp_match_created', reverse: true };
  }
};

export const getInterestScheduleContextConfig = (interestScheduleConfig: InterestScheduleConfig) => {
  const result: InterestScheduleContextConfig = {
    ...omit(interestScheduleConfig, interestScheduleConfigGroupKeys),
    exportGroups: interestScheduleConfig.exportGroups,
    statusDropdownOptions: [],
    statusGroups: interestScheduleConfig.presentGroups,
    statusGroupsActive: interestScheduleConfig.presentGroupsActive,
    statusOptions: [],
  };
  result.statusDropdownOptions = getAllInterestScheduleStatusDropdownOptions(result);
  result.statusOptions = getAllInterestScheduleStatusOptions(result);

  return result;
};

export const getAllInterestScheduleStatusOptions = (contextConfig: InterestScheduleContextConfig) => {
  return contextConfig.statusGroups.reduce((result, group) => {
    const groupStatuses = contextConfig.groupConfig[group].statuses;

    if (groupStatuses) {
      groupStatuses.forEach((status) => {
        result.push({ id: status as InterestScheduleStatuses, label: contextConfig.statusConfig[status].label });
      });
    }

    return result;
  }, [] as Option<InterestScheduleStatuses>[]);
};

export const getAllInterestScheduleStatusDropdownOptions = (contextConfig: InterestScheduleContextConfig) => {
  const statusOptions = contextConfig.statusGroups.reduce((result, group) => {
    const groupStatuses = contextConfig.groupConfig[group].statuses;

    if (groupStatuses) {
      groupStatuses.forEach((status) => {
        result.push({ text: contextConfig.statusConfig[status].label, value: status as InterestScheduleStatuses });
      });
    }

    return result;
  }, [] as InterestScheduleStatusDropdownOption[]);

  // Discount / Clear Discounted
  statusOptions.push({ divider: true, text: '' });
  statusOptions.push({
    value: InterestScheduleCustomAction.Discount,
    text: t('discount'),
  });
  statusOptions.push({
    value: InterestScheduleCustomAction.ClearDiscountedReason,
    text: t('clear_discounted_reason'),
  });

  // Un-archive / Archive
  statusOptions.push({ divider: true, text: '' });
  statusOptions.push({
    value: InterestScheduleCustomAction.ShowOnSchedule,
    text: t('un_hyphen_archive_to_schedule'),
  });
  statusOptions.push({
    value: InterestScheduleCustomAction.HideFromSchedule,
    text: t('archive_from_schedule'),
  });

  return statusOptions;
};

export const getInterestScheduleTableColumn = <
  TField extends ReportingInterestColumnKeys = ReportingInterestColumnKeys
>(
  field: TField,
  name: string,
  pinned?: ColumnPinningPosition
): TableColumn<TField> => {
  const column: TableColumn<TField> = { field, name, pinned };

  switch (field) {
    // ---- Custom ----
    case 'fav':
      column.width = 35;
      break;

    // ---- Selectable ----

    case 'id':
      column.width = 80;
      break;
    case 'price_from':
    case 'price_to':
    case 'rent_from':
    case 'rent_metric':
    case 'rent_to':
      column.width = 100;
      break;
    case 'investment_lot_size_from':
    case 'investment_lot_size_to':
    case 'lease_expiry':
    case 'next_lease_break':
    case 'tenure':
      column.width = 120;
      break;
    case 'interest_schedule_updated_at':
      column.width = 130;
      break;
    case 'date_of_requirement':
    case 'requirement_created':
    case 'requirement_updated':
      column.width = 190;
      break;

    case 'agent_company':
    case 'agent_contact_email':
    case 'agent_contact_mobile':
    case 'agent_contact_telephone':
    case 'agent_contact':
    case 'agent':
    case 'created_by':
    case 'current_tenancy':
    case 'details':
    case 'discounted_reason_string':
    case 'interest_schedule_updated_by':
    case 'investment_lot_size':
    case 'investment_profile':
    case 'investor':
    case 'match_created':
    case 'match_demise':
    case 'matched_by':
    case 'investment_minimum_net_yield':
    case 'property_type':
    case 'record_owner':
    case 'requirement_status_readable':
    case 'requirement_status':
    case 'search_area':
    case 'sectors':
    case 'size_from_sqft':
    case 'size_to_sqft':
    case 'size':
    case 'source':
    case 'submarkets':
    case 'target_occupancy':
    case 'tenant_company':
    case 'tenant_contact_email':
    case 'tenant_contact_mobile':
    case 'tenant_contact_telephone':
    case 'tenant_contact':
    case 'tenant_name':
    case 'viewing_date':
    case 'viewings_completed':
      column.width = 200;
      break;

    case 'status':
    case 'tenant_sector':
      column.width = 220;
      break;

    case 'completed_date':
      column.width = 225;
      break;

    case 'title':
    case 'all_match_comments':
      column.width = 240;
      break;

    case 'latest_comment':
      column.width = 400;
      break;
  }

  return column;
};

export const getInterestScheduleStatusLabelTooltip = (
  interest: PartialDisposalInterestIndexItem,
  contextConfig: InterestScheduleContextConfig
) => {
  let label = contextConfig.statusLabelsMap[interest.interest_schedule_status] || t('unknown');
  let tooltip = '';

  const isDiscounted = !!interest.interest_schedule_discounted_reason;
  const isNoLongerActive = !!interest.requirement_no_longer_active;

  if (interest.interest_schedule_status === InterestScheduleStatuses.Outbound) {
    label = t('match_requested');
  }

  if (isNoLongerActive) {
    tooltip = t('requirement_no_longer_active');
  } else if (isDiscounted) {
    tooltip = t('match_discounted_due_to_quote_xreasonx_quote', {
      reason: interest.interest_schedule_discounted_reason_string,
    });
  }

  return { label, tooltip };
};

export const getMatchSearchHash = (interest: DisposalInterestIndexItem): string => {
  const items: string[] = [
    interest.interest_column_values.agent_company,
    interest.interest_column_values.agent_contact_email,
    interest.interest_column_values.agent_contact_mobile,
    interest.interest_column_values.agent_contact_telephone,
    interest.interest_column_values.agent_contact,
    interest.interest_column_values.investor,
    interest.interest_column_values.matched_by,
    interest.interest_column_values.record_owner,
    interest.interest_column_values.search_area,
    interest.interest_column_values.sectors,
    interest.interest_column_values.source.source_string ?? '',
    interest.interest_column_values.status,
    interest.interest_column_values.submarkets,
    interest.interest_column_values.tenant_contact_email,
    interest.interest_column_values.tenant_contact_mobile,
    interest.interest_column_values.tenant_contact_telephone,
    interest.interest_column_values.tenant_contact,
    interest.interest_column_values.tenant_name,
  ];

  return items
    .filter((item) => item)
    .join(' ')
    .toLowerCase();
};

export const sortInterestScheduleByOrder = (
  interestA: DisposalInterestIndexItem,
  interestB: DisposalInterestIndexItem,
  orderBy: OrderByConfig
): number => {
  if (!interestA || !interestB) {
    return 0;
  }
  if (interestA[orderBy.key] === interestB[orderBy.key]) {
    return 0;
  }

  const comparison = interestA[orderBy.key] > interestB[orderBy.key] ? 1 : -1;

  return orderBy.reverse ? -comparison : comparison;
};
